import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Icon from "./Icon";
import { useSelector } from "react-redux";
import { selectUser } from "../slices/userReducer";
import { adminUserIds } from "../helpers/helpers";
import { supabase } from "../supabaseClient";
import { LogoSvg } from "../utils/svg";
import SuggestionsModal from "./SuggestionsModal";

import UserIcon from "../assets/icons/Account.png";
import CalendarIcon from "../assets/icons/Planner.png";
import DealIcon from "../assets/icons/Discount.png";
import SpinAndWinIcon from "../assets/icons/Community ticket.png";
import RequestIcon from "../assets/icons/Communication.png";

import LanguageIcon from "../assets/icons/Globe.png";
import TiktokIcon from "../assets/icons/TikTok.png";
import InstagramIcon from "../assets/icons/Instagram.png";

const SidebarOption = ({
  option,
  currentSlug,
  cities,
  activeDropdown,
  setActiveDropdown,
}) => {
  const [hovering, setHovering] = useState(false);

  if (option.showCitiesDropdown) {
    return (
      <div
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className={`${
          hovering || option.activeLink === currentSlug ? "bg-[#D0ADFB20]" : ""
        } rounded-[4px]`}
      >
        <button
          onClick={() => {
            setActiveDropdown(activeDropdown === option.id ? null : option.id);
          }}
          className={`!text-neutral-300 transition-all font-normal flex gap-[8px] items-center justify-between p-[8px] rounded-[4px] text-[16px] leading-[22px] w-full ${
            option.activeLink === currentSlug
              ? "!text-white !font-semibold"
              : "hover:text-white hover:!font-medium"
          }`}
        >
          <div className="flex items-center gap-[8px]">
            <img
              src={option.icon}
              className={`${
                option.activeLink === currentSlug ? "brightness-0 invert" : ""
              } w-[25px] h-[25px]`}
              alt={option.name}
            />
            <p className="text-[16px]">{option.name}</p>
          </div>
          <Icon
            icon={
              activeDropdown === option.id
                ? "heroicons:chevron-up"
                : "heroicons:chevron-down"
            }
            className="w-[16px] h-[16px]"
          />
        </button>

        <div
          className="pl-[8px] overflow-hidden transition-all duration-300"
          style={{
            maxHeight: activeDropdown === option.id ? "300px" : "0px",
            opacity: activeDropdown === option.id ? 1 : 0,
          }}
        >
          <div className="pr-2 max-h-[300px] overflow-y-auto custom-scrollbar">
            <Link
              to={
                window.location.pathname.includes("admin")
                  ? "/admin"
                  : "/?city=all_cities"
              }
              className={`mb-[4px] p-[8px] flex items-center gap-[8px] rounded-[4px] font-normal !text-neutral-300 hover:bg-[#D0ADFB20] hover:text-white hover:!font-medium transition-all`}
            >
              <p className="text-[14px]">All Events</p>
            </Link>
            {cities.map((city) => (
              <Link
                key={city.id}
                to={
                  window.location.pathname.includes("admin")
                    ? `/admin/events/${city.name}`
                    : `/?city=${city.name}`
                }
                className={`mb-[4px] p-[8px] flex items-center gap-[8px] rounded-[4px] font-normal !text-neutral-300 hover:bg-[#D0ADFB20] hover:text-white hover:!font-medium transition-all`}
              >
                <p className="text-[14px]">{city.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={option.link}
      key={option.id}
      className={`!text-neutral-300 transition-all font-normal flex gap-[8px] items-center p-[8px] rounded-[4px] text-[16px] leading-[22px] ${
        option.activeLink === currentSlug
          ? "bg-[#D0ADFB20] !text-white !font-semibold"
          : "hover:bg-[#D0ADFB20] hover:text-white hover:!font-medium"
      }`}
    >
      <img
        src={option.icon}
        className={`${
          option.activeLink === currentSlug ? "brightness-0 invert" : ""
        } w-[25px] h-[25px]`}
        alt={option.name}
      />
      <p className="text-[16px]">{option.name}</p>
    </Link>
  );
};

const Sidebar = ({ language, setLanguage, isMobile, onClose }) => {
  const currentSlug = window.location.href.split("?")[0].split("/").pop();
  const user = useSelector(selectUser);
  const [cities, setCities] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);

  const sidebarOptions = [
    ...(user.id
      ? [
          {
            id: "account",
            icon: UserIcon,
            name: "Account",
            link: "/profile",
            activeLink: "profile",
          },
        ]
      : [
          {
            id: "login",
            icon: UserIcon,
            name: "Log in",
            link: "/email-login",
            activeLink: "email-login",
          },
        ]),
    {
      id: "events",
      icon: CalendarIcon,
      name: "Events",
      link: "/",
      activeLink: "",
      showCitiesDropdown: true,
    },
    {
      id: "deals",
      icon: DealIcon,
      name: "Deals",
      link: "/lifestyle",
      activeLink: "lifestyle",
    },
    {
      id: "spin-and-win",
      icon: SpinAndWinIcon,
      name: "Win tickets",
      link: "/spin-and-win",
      activeLink: "spin-and-win",
    },
    {
      id: "request-event",
      icon: RequestIcon,
      name: "Community",
      link: "/community-chats",
      activeLink: "community-chats",
    },
    ...(adminUserIds.includes(user.id)
      ? [
          {
            id: "admin",
            icon: UserIcon,
            name: "Admin",
            link: "/admin",
            activeLink: "admin",
          },
        ]
      : []),
  ];

  const bottomOptions = [
    {
      id: "instagram",
      icon: InstagramIcon,
      name: "Instagram",
      link: "https://www.instagram.com/22night.worldwide/",
      activeLink: "instagram",
      target: "_blank",
    },
    {
      id: "tiktok",
      icon: TiktokIcon,
      name: "Tiktok",
      link: "https://www.tiktok.com/@22night.worldwide",
    },
  ];

  useEffect(() => {
    // Fetch cities that have active events
    supabase
      .from("Events")
      .select("city")
      .gte("datetime", new Date().toISOString())
      .then((res) => {
        if (res.data) {
          // Get unique cities with active events
          const uniqueCities = [
            ...new Set(res.data.map((event) => event.city)),
          ];

          // Fetch full city details for cities with active events
          supabase
            .from("City")
            .select("*")
            .in("name", uniqueCities)
            .order("index", { ascending: true })
            .then((cityRes) => {
              setCities(cityRes.data || []);
            });
        }
      });
  }, []);

  return (
    <>
      <SuggestionsModal
        show={showSuggestionsModal}
        setShow={setShowSuggestionsModal}
      />
      <div
        className="top-0 z-[999] fixed flex flex-col justify-between px-[20px] py-[40px] lg:border-[#AD7AEF50] lg:border-r lg:w-[250px] max-lg:w-full h-[100vh] gradient-background"
      >
        <div className="flex flex-col justify-between gap-[24px]">
          <div className="lg:hidden flex justify-between items-center-w-full">
            <button onClick={() => onClose()} className="text-white">
              <Icon icon="heroicons:x-mark" className="w-[24px] h-[24px]" />
            </button>
            <Link to="/" className="flex items-center">
              <LogoSvg />
            </Link>
            <div className="w-[24px] h-[24px]" />
          </div>
          <Link
            to="/"
            className="max-lg:hidden flex items-center px-[12px] py-[16px] h-[64px]"
          >
            <LogoSvg />
          </Link>
          <div className="flex flex-col gap-[8px]">
            {sidebarOptions.map((option) => (
              <SidebarOption
                key={option.id}
                option={option}
                currentSlug={currentSlug}
                cities={cities}
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-[8px]">
          <div className="flex items-center gap-[6px] hover:bg-[#D0ADFB1A] hover:shadow-lg p-[8px] rounded-[4px] font-normal text-[16px] leading-[22px]">
            <img
              src={LanguageIcon}
              className="w-[25px] h-[25px]"
              alt="Language"
            />
            <select
              value={"language"}
              onChange={(e) => {
                setLanguage(e.target.value);
              }}
              className="bg-transparent !border-none rounded-lg !outline-none focus:ring-0 w-[90%] font-normal !text-neutral-300 text-[16px] appearance-none cursor-pointer"
            >
              <option value="language" disabled>
                Language
              </option>
              <option value="nl">Nederlands</option>
              <option value="en">English</option>
            </select>
          </div>
          {bottomOptions.map((option) => (
            <SidebarOption
              key={option.id}
              option={option}
              currentSlug={currentSlug}
              cities={cities}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
